import { useMemo } from "react";
import { Breadcrumbs, ClickAwayListener, Grow, Link, MenuItem, MenuList, Paper, Popper, Stack, TablePagination, Typography } from "@mui/material";
import { useFuncionariosListaViewModel } from "../../viewModels/funcionarios_lista_view_model";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { Header } from "./components/header";
import { GenerateColumnsDefinition } from "./columns";
import { ReportsFuncionarios } from "./components/reports_funcionarios";
import { ReportsTreinamentos } from "./components/reports_treinamentos";
import { ReportsDocumentos } from "./components/reports_documentos";
import { AlterarCentroCustoModal } from "./components/alterar_centro_custo";
import { GRID_CHECKBOX_SELECTION_FIELD, GridPinnedColumnFields } from "@mui/x-data-grid-pro";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { LegendaModal } from "./components/legenda_modal";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";
import { FuncionarioCopyModal } from "./components/funcionario_copy_modal";

import AlertError from "../../../../../common/components/alert_error";
import RefreshButton from "../../../../../common/components/refresh_button";
import PrimaryButton from "../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * FuncionariosLista
 */

export const FuncionariosLista = () => {
  const viewModel = useFuncionariosListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateColumnsDefinition({
      onActionEdit: viewModel.handleFuncionarioEdit,
      onActionDelete: viewModel.handleFuncionarioDelete,
      onActionCopy: viewModel.handleFuncionarioCopyModalOpen,
      onExportar: viewModel.handleFuncionarioExportar,
    });
  }, [
    viewModel.handleFuncionarioEdit,
    viewModel.handleFuncionarioDelete,
    viewModel.handleFuncionarioCopyModalOpen,
    viewModel.handleFuncionarioExportar,
  ]);

  const pinnedColumns = useMemo((): GridPinnedColumnFields => {
    return viewModel.funcionarios.length > 0 ?
      {
        left: [
          GRID_CHECKBOX_SELECTION_FIELD,
        ],
        right: [
          'idFuncionario',
          'copiar',
        ],
      } : {
      };
  }, [
    viewModel.funcionarios,
  ]);

  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[180px] ">
                <PrimaryButton
                  buttonColor="secondary"
                  onClick={() => viewModel.handleOpenAlterarCentroCustoModal(true)}>
                  Alterar Centro Custo
                </PrimaryButton>
              </div>

              <div>
                <Stack direction="row" spacing={2}>
                  <div className="w-[12rem]">
                    <PrimaryButton
                      ref={viewModel.menuRef}
                      id="composition-button"
                      aria-controls={viewModel.openMenu ? 'composition-menu' : undefined}
                      aria-expanded={viewModel.openMenu ? 'true' : undefined}
                      aria-haspopup="true"
                      onClick={viewModel.handleToggleMenu}
                      buttonColor="yellow">
                      Relatórios
                    </PrimaryButton>
                    <Popper
                      style={{ zIndex: 10000 }}
                      open={viewModel.openMenu}
                      anchorEl={viewModel.menuRef.current}
                      role={undefined}
                      placement="bottom-start"
                      transition
                      disablePortal
                    >
                      {({ TransitionProps, placement }) => (
                        <Grow
                          {...TransitionProps}
                          style={{
                            transformOrigin:
                              placement === 'bottom-start' ? 'left top' : 'left bottom',
                          }}
                        >
                          <Paper>
                            <ClickAwayListener onClickAway={viewModel.handleCloseMenu}>
                              <MenuList
                                autoFocusItem={viewModel.openMenu}
                                id="composition-menu"
                                aria-labelledby="composition-button"
                                onKeyDown={viewModel.handleListKeyDown}>
                                <MenuItem onClick={() => viewModel.handleReportsFuncionariosOpenState(true)}>Funcionários</MenuItem>
                                <MenuItem onClick={() => viewModel.handleReportsTreinamentosOpenState(true)}>Treinamentos</MenuItem>
                                <MenuItem onClick={() => viewModel.handleReportsDocumentosOpenState(true)}>Documentos Funcionários</MenuItem>
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </div>
                </Stack>
              </div>

              <div className="w-[180px]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    if (e.button === 1) {
                      viewModel.handleNewFuncionario(true);
                    } else {
                      viewModel.handleNewFuncionario(e.ctrlKey);
                    }
                    e.preventDefault();
                  }}>
                  Novo Funcionário
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-4 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            RH
          </Link>
          <Typography className="text-primary-green">
            Funcionários
          </Typography>
        </Breadcrumbs>

        <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-3xl font-medium text-black dark:text-white">Funcionários</h1>
            <RefreshButton onClick={viewModel.handleReloadPage} />
          </div>

          <div className="w-full justify-end flex gap-2">
          </div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReloadPage} />
              </div>
            ) : (
              <>
                <Header
                  tipoPesquisa={viewModel.filters.tipoPesquisa}
                  pesquisa={viewModel.filters.pesquisa}
                  setor={viewModel.filters.setor}
                  filial={viewModel.filters.filial}
                  admissaoDe={viewModel.filters.admissaoDe}
                  admissaoAte={viewModel.filters.admissaoAte}
                  demissaoDe={viewModel.filters.demissaoDe}
                  demissaoAte={viewModel.filters.demissaoAte}
                  afastamentoDe={viewModel.filters.afastamentoDe}
                  afastamentoAte={viewModel.filters.afastamentoAte}
                  feriasDe={viewModel.filters.feriasDe}
                  feriasAte={viewModel.filters.feriasAte}
                  centroDeCusto={viewModel.filters.centroDeCusto}
                  status={viewModel.filters.status}
                  treinamentos={viewModel.filters.treinamentos}
                  prazoExperiencia={viewModel.filters.prazoExperiencia}
                  documentos={viewModel.filters.documentos}
                  centroDeCustoLista={viewModel.filters.centroDeCustoLista ?? []}
                  handleTipoPesquisa={viewModel.handleTipoPesquisaChange}
                  handlePesquisa={viewModel.handlePesquisaChange}
                  handleSetor={viewModel.handleSetorChange}
                  handleFilial={viewModel.handleFilialChange}
                  handleAdmissao={viewModel.handleAdmissaoChange}
                  handleDemissao={viewModel.handleDemissaoChange}
                  handleAfastamento={viewModel.handleAfastamentoChange}
                  handleFerias={viewModel.handleFeriasChange}
                  handleCentroDeCusto={viewModel.handleCentroDeCustoChange}
                  handleStatusSelection={viewModel.handleStatusChange}
                  handleTreinamento={viewModel.handleTreinamentoChange}
                  handlePrazoExperiencia={viewModel.handlePrazoExperienciaChange}
                  handleDocumentos={viewModel.handleDocumentosChange}
                  handleClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-stretch">
                    <div className="flex flex-col justify-end my-2">
                      <span
                        className="text-[#009C4E] bg-transparent border border-[#009C4E] px-2 py-1 text-sm rounded rounded-base font-semibold cursor-pointer"
                        onClick={() => viewModel.handleShowLegenda(true)}>
                        Legenda
                      </span>
                    </div>

                    <div className="flex flex-col justify-between flex-1">
                      {viewModel.pageSize !== undefined &&
                        <div className="flex flex-col flex-1 items-end">
                          <div className="flex flex-col items-center">
                            <span className="bg-red-500 text-sm text-white py-1 px-3 rounded">Espaço Utilizado:</span>
                            <span className="font-bold text-black dark:text-white mt-4">{viewModel.pageSize} MB</span>
                          </div>
                        </div>
                      }

                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <StyledDataGrid
                    tableName="funcionarios_lista"
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={false}
                    textSize="pequeno"
                    getRowId={(row) => row.idFuncionario}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    checkboxSelection={true}
                    getRowHeight={() => 'auto'}
                    onRowSelectionModelChange={viewModel.handleRowSelectionModelChange}
                    rowSelectionModel={viewModel.rowSelectionModel}
                    rows={viewModel.funcionarios}
                    rowCount={viewModel.funcionarios.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    pinnedColumns={pinnedColumns}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                </Stack>
              </>
            )
        }

        {/**
          * Modals
          */}

        <LegendaModal
          open={viewModel.openLegendaModal}
          onClose={() => viewModel.handleShowLegenda(false)}
        />

        <AlterarCentroCustoModal
          open={viewModel.alterarCentroCustoModal}
          saving={viewModel.saving}
          centroCustos={viewModel.filters.centroDeCustoLista ?? []}
          onClose={() => viewModel.handleOpenAlterarCentroCustoModal(false)}
          onSubmit={viewModel.handleAlterarCentroCustoSubmit}
        />

        <FuncionarioCopyModal
          open={viewModel.funcionarioCopyModal}
          saving={viewModel.saving}
          loading={viewModel.loading}
          selectedFuncionario={viewModel.selectedFuncionario}
          centroCustoDropdown={viewModel.filters.centroDeCustoLista ?? []}
          cargoDropdown={viewModel.filters.cargoLista ?? []}
          onClose={() => viewModel.handleFuncionarioCopyModalOpen(null, false)}
          onSubmit={viewModel.handleFuncionarioCopySubmit}
        />

        <ReportsFuncionarios
          open={viewModel.openReportsFuncionarios}
          saving={viewModel.saving}
          loading={viewModel.loading}
          funcionarioNomesLista={viewModel.filters.funcionarioNomesLista ?? []}
          funcionarioSetoresLista={viewModel.filters.funcionarioSetoresLista ?? []}
          funcionarioFiliaisLista={viewModel.filters.funcionarioFiliaisLista ?? []}
          centroCustoLista={viewModel.filters.centroDeCustoLista ?? []}
          onClose={() => viewModel.handleReportsFuncionariosOpenState(false)}
          onSubmit={viewModel.handleReportsFuncionarios}
        />

        <ReportsTreinamentos
          open={viewModel.openReportsTreinamentos}
          saving={viewModel.saving}
          loading={viewModel.loading}
          funcionarioNomesLista={viewModel.filters.funcionarioNomesLista ?? []}
          centroCustoLista={viewModel.filters.centroDeCustoLista ?? []}
          treinamentosDropdown={viewModel.filters.treinamentosDropdown ?? []}
          cargosDropdown={viewModel.filters.cargoLista ?? []}
          onClose={() => viewModel.handleReportsTreinamentosOpenState(false)}
          onSubmit={viewModel.handleReportsTreinamentos}
        />

        <ReportsDocumentos
          open={viewModel.openReportsDocumentos}
          saving={viewModel.saving}
          loading={viewModel.loading}
          funcionarioNomesLista={viewModel.filters.funcionarioNomesLista ?? []}
          centroCustoLista={viewModel.filters.centroDeCustoLista ?? []}
          onClose={() => viewModel.handleReportsDocumentosOpenState(false)}
          onSubmit={viewModel.handleReportsDocumentos}
        />

        <DeleteDialog
          open={viewModel.openDeleteDialog}
          saving={viewModel.saving}
          onClose={viewModel.handleCloseDeleteDialog}
          title={`Você realmente deseja excluir o funcionário ${viewModel.selectedFuncionario?.nome}?`}
          onDelete={viewModel.handleDeleteFuncionario}
        />

        <ReportViewerModal
          reportViewerState={viewModel.reportViewerState}
        />

      </div>
    </>
  );
}